import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { FormProvider, useForm } from "react-hook-form"
import { Stack, Typography } from "@mui/material"

import ControlledInput from "widgets/common/ControlledInput"
import PopUp from "widgets/common/PopUp"
import { USER_ROLES, USER_ROLES_WITHOUT_OWNER } from "helpers/utils/constants"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import { useUpdateOrgUserMutation } from "features/api"
import { selectCurrentOrgId } from "features/store/orgSlice"
import FormButtons from "widgets/common/FormButtons"
import usePermissions from "helpers/hooks/usePermissions"

interface IEditUserForm {
  username: string
  role: keyof typeof USER_ROLES
}

interface EditUserModalProps {
  open: boolean
  onClose: () => void
  username: string
  role: keyof typeof USER_ROLES
}

const EditUserModal = ({ open, onClose, username, role }: EditUserModalProps) => {
  const { t } = useTranslation()
  const { isOwner } = usePermissions()
  const orgId = useSelector(selectCurrentOrgId)
  const methods = useForm<IEditUserForm>({
    mode: "all",
    defaultValues: {
      username,
      role,
    },
  })

  const ROLE_OPTIONS_BY_ROLE = useMemo(
    () => (isOwner ? USER_ROLES : USER_ROLES_WITHOUT_OWNER),
    [isOwner],
  )

  useEffect(() => {
    methods.reset({ role })
  }, [methods, role])

  const [updateUser, { isLoading: isUpdating }] = useUpdateOrgUserMutation()

  const roles = useMemo(
    () =>
      Object.entries(ROLE_OPTIONS_BY_ROLE).map(([key, value]) => ({
        value,
        label: t(`roles.${key.toUpperCase()}`, key),
      })),
    [t, ROLE_OPTIONS_BY_ROLE],
  )

  const onSubmit = async ({ role }: IEditUserForm) => {
    snackbarMutation({
      mutation: updateUser({
        username,
        orgId: orgId as number,
        role,
      }).unwrap(),
      getErrorMessage: buildGetErrorMessage(
        t("error.UPDATING_ITEM", { item: t("generic.USER") }),
      ),
      getSuccessMessage: () => t("users.USER_UPDATED"),
    })
      .then(() => {
        methods.reset()
        onClose()
      })
      .catch(() => {})
  }

  return (
    <PopUp
      open={open}
      onClose={() => {
        methods.reset()
        onClose()
      }}
      titleKey="users.EDIT_USER"
      fullWidth
      maxWidth={"sm"}
    >
      <Stack>
        <Typography variant="h6" mb={3} align="center">
          {username}
        </Typography>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <FormProvider {...methods}>
            <ControlledInput
              type="select"
              placeholder=""
              name="role"
              label={t("generic.ROLE")}
              selectOptions={roles}
              variant="outlined"
            />
            <FormButtons
              onClose={() => {
                methods.reset()
                onClose()
              }}
              buttonText={"generic.UPDATE"}
              isLoading={isUpdating}
            />
          </FormProvider>
        </form>
      </Stack>
    </PopUp>
  )
}

export default EditUserModal
